// src/services/provider.js

// Base API URL - consider moving to an environment variable
export const API_BASE_URL = `${import.meta.env.VITE_API_URL}/api`;
const API_KEY = import.meta.env.VITE_API_KEY

/**
 * Fetch all providers or search with a query
 * @param {string} query - Optional search query
 * @param {string} language - Optional language filter
 * @returns {Promise<Array>} - Array of provider objects
 */
export async function fetchProviders(query = '', language = '', page = 1, pageSize = 10) {
  let url = `${API_BASE_URL}/providers`;
  
  // Add query parameters
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  params.append('language', language || 'english');
  params.append('page', page.toString());
  params.append('per_page', pageSize.toString()); // Changed from 'page_size' to 'per_page'
  
  url += `?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  return {
    providers: data.providers || [],
    pagination: {
      total: data.total || 0,
      page: data.page || 1,
      page_size: data.page_size || 10,
      total_pages: data.total_pages || 1
    }
  };
}

/**
 * Fetch providers for validator review (includes raw/agency data)
 * @param {string} query - Optional search query
 * @param {string} language - Optional language filter
 * @param {number} page - Page number
 * @param {number} pageSize - Number of items per page
 * @param {string} source - Optional source filter ('agency' or 'provider')
 * @param {Array<string>} status - Optional status filter array
 * @returns {Promise<Object>} - Object with providers array and pagination info
 */
export async function fetchProvidersForValidator(token, query = '', language = '', page = 1, pageSize = 10, source = null, status = null , city = null, state = null) {
  let url = `${API_BASE_URL}/providers/protected/search/validator`;
  
  // Add query parameters
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  params.append('language', language || 'english');
  params.append('page', page.toString());
  params.append('per_page', pageSize.toString());
  if (source) params.append('source', source);
  if (status) params.append('status', status); 
  if (city) params.append('city', city);
  if (state) params.append('state', state);
  
  url += `?${params.toString()}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY,
      'Authorization': `Bearer ${token}` 
    }
  });
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  
  return {
    providers: data.providers || [],
    pagination: {
      total: data.total || 0,
      page: data.page || 1,
      page_size: data.page_size || 10,
      total_pages: data.total_pages || 1
    }
  };
}

/**
 * Get a single provider by ID
 * @param {string|number} id - Provider ID
 * @returns {Promise<Object>} - Provider object
 */
export async function getProvider(id, language = 'english') {
  const response = await fetch(`${API_BASE_URL}/providers/${id}?language=${encodeURIComponent(language)}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Get the current user's provider profile
 * @param {string} token - JWT token for authentication
 * @param {string} language - Language for localization (default: 'english')
 * @returns {Promise<Object>} - Provider profile or status message
 */
export async function fetchProviderProfile(token, language = 'english') {
  const response = await fetch(
    `${API_BASE_URL}/providers/protected/profile?language=${encodeURIComponent(language)}`, 
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    }
  );
  
  // Special handling for 404 - profile not found
  if (response.status === 404) {
    const data = await response.json();
    // Return the data with status so we can check it
    return data; // This will be { message: "...", status: "not_found" }
  }
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Get phone numbers for a provider by ID
 * @param {string|number} id - Provider ID
 * @returns {Promise<string[]>} - Array of phone numbers
 */
export async function getProviderPhoneNumbers(id) {
  const response = await fetch(`${API_BASE_URL}/providers/${id}/phone-numbers`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': API_KEY
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

/**
 * Format provider data from API to form-friendly format
 * @param {Object} providerData - Provider data from API 
 * @returns {Object} - Formatted provider data for forms
 */
export function formatProviderDataForForm(providerData) {
  if (!providerData) return null;
  
  // Extract provider data from the response (which might have a provider key or be direct)
  const provider = providerData.provider || providerData;
  
  // Format additional_locations from strings to objects
  const additionalLocations = Array.isArray(provider.additional_locations) 
    ? provider.additional_locations.map(loc => {
        // Try to parse location string like "123 Main St, City, State 12345"
        const parts = loc.split(',').map(part => part.trim());
        const postalCode = parts.length > 2 ? parts[2].split(' ').pop() : '';
        const state = parts.length > 2 ? parts[2].split(' ')[0] : '';
        
        return {
          address: parts[0] || '',
          city: parts.length > 1 ? parts[1] : '',
          state: state || '',
          postal_code: postalCode || ''
        };
      })
    : [];
  
  // Format social_media from object to array of objects
  const socialMedia = provider.social_media && typeof provider.social_media === 'object'
    ? Object.entries(provider.social_media).map(([platform, url]) => ({
        platform,
        url
      }))
    : [];
  
  // Format office_hours from object to array of objects
  const officeHours = provider.office_hours && typeof provider.office_hours === 'object'
    ? Object.entries(provider.office_hours).flatMap(([day, slots]) => 
        Array.isArray(slots) ? slots.map(slot => ({
          day,
          open: slot.open || '',
          close: slot.close || ''
        })) : []
      )
    : [];
  
  return {
    // Basic info
    id: provider.id || '',
    provider_name: provider.provider_name || '',
    provider_type: provider.provider_type || '',
    status: provider.status || 'unverified',
    slug: provider.slug || '',
    
    // Address
    address_line1: provider.address_line1 || '',
    address_line2: provider.address_line2 || '',
    address_line3: provider.address_line3 || '',
    city: provider.city || '',
    state: provider.state || '',
    postal_code: provider.postal_code || '',
    latitude: provider.latitude || null,
    longitude: provider.longitude || null,
    
    // Contact and specialties
    phone_numbers: Array.isArray(provider.phone_numbers) ? provider.phone_numbers : [],
    specializations: Array.isArray(provider.specializations) ? provider.specializations : [],
    procedures: Array.isArray(provider.procedures) ? provider.procedures : [],
    insurance_accepted: Array.isArray(provider.insurance_accepted) ? provider.insurance_accepted : [],
    
    // Locations and wait time
    additional_locations: additionalLocations,
    average_wait_time_minutes: provider.average_wait_time_minutes || null,
    
    // Additional info
    additional_info: provider.additional_info || '',
    tags: Array.isArray(provider.tags) ? provider.tags : [],
    
    // New fields - languages and education
    spoken_languages: Array.isArray(provider.spoken_languages) ? provider.spoken_languages : [],
    education: Array.isArray(provider.education) ? provider.education : [],
    certifications: Array.isArray(provider.certifications) ? provider.certifications : [],
    years_of_experience: provider.years_of_experience || null,
    
    // Patient-related flags
    accepts_new_patients: provider.accepts_new_patients === true,
    telehealth_available: provider.telehealth_available === true,
    
    // Hours and contact
    office_hours: officeHours,
    parking_info: provider.parking_info || '',
    website_url: provider.website_url || '',
    social_media: socialMedia,
    
    // Payment and accessibility
    payment_methods: Array.isArray(provider.payment_methods) ? provider.payment_methods : [],
    accessibility_options: Array.isArray(provider.accessibility_options) ? provider.accessibility_options : [],
    
    // Optional fields
    verification_info: provider.verification_info || null,
    views: provider.views || 0,
    is_draft: provider.status === 'draft'
  };
}

/**
 * Format form data to API-friendly format
 * @param {Object} formData - Provider form data
 * @returns {Object} - API-friendly provider data
 */
export function formatFormDataForApi(formData) {
  // Helper function to filter out blank strings from arrays
  const filterBlankStrings = (arr) => arr.filter(item => item && item.trim() !== '');

  // Helper function to filter out blank objects (objects with no meaningful values)
  const filterBlankObjects = (arr) => arr.filter(item => {
    if (typeof item === 'string') return item && item.trim() !== '';
    if (typeof item === 'object' && item !== null) {
      return Object.values(item).some(val => val !== null && val !== undefined && val !== '');
    }
    return false;
  });

  // Transform additional_locations from array of objects to array of strings
  const additionalLocations = Array.isArray(formData.additional_locations)
    ? formData.additional_locations
        .filter(loc => loc.address || loc.city || loc.state || loc.postal_code) // Filter out blank entries
        .map(loc => 
          `${loc.address || ''}, ${loc.city || ''}, ${loc.state || ''} ${loc.postal_code || ''}`
        )
    : [];

  // Transform social_media from array of objects to key-value map
  const socialMedia = Array.isArray(formData.social_media)
    ? formData.social_media
        .filter(item => item.platform && item.url) // Only include if both platform and url exist
        .reduce((obj, item) => {
          obj[item.platform] = item.url;
          return obj;
        }, {})
    : {};

  // Transform office_hours from array of objects to map with days as keys
  const officeHours = Array.isArray(formData.office_hours)
    ? formData.office_hours
        .filter(item => item.day && (item.open || item.close)) // Filter out entries without day or times
        .reduce((obj, item) => {
          if (!obj[item.day]) {
            obj[item.day] = [];
          }
          obj[item.day].push({
            open: item.open || '',
            close: item.close || ''
          });
          return obj;
        }, {})
    : {};

  return {
    // Include ID if we're updating
    ...(formData.id ? { id: formData.id } : {}),

    // Basic info
    provider_name: formData.provider_name || '',
    provider_type: formData.provider_type || '',
    ...(formData.id ? {} : { status: 'unverified' }),
    

    // Address
    address_line1: formData.address_line1 || '',
    address_line2: formData.address_line2 || '',
    address_line3: formData.address_line3 || '',
    city: formData.city || '',
    state: formData.state || '',
    postal_code: formData.postal_code ? parseInt(formData.postal_code, 10) : null,

    // Contact and specialties
    phone_numbers: Array.isArray(formData.phone_numbers) ? filterBlankStrings(formData.phone_numbers) : [],
    specializations: Array.isArray(formData.specializations) ? filterBlankStrings(formData.specializations) : [],
    procedures: Array.isArray(formData.procedures) ? filterBlankStrings(formData.procedures) : [],
    insurance_accepted: Array.isArray(formData.insurance_accepted) ? filterBlankStrings(formData.insurance_accepted) : [],

    // Locations and wait time
    additional_locations: additionalLocations,
    average_wait_time_minutes: formData.average_wait_time_minutes ? parseInt(formData.average_wait_time_minutes, 10) : null,

    // Additional info
    additional_info: formData.additional_info || '',
    tags: Array.isArray(formData.tags) ? filterBlankStrings(formData.tags) : [],

    // New fields - languages and education
    spoken_languages: Array.isArray(formData.spoken_languages) ? filterBlankStrings(formData.spoken_languages) : [],
    education: Array.isArray(formData.education) ? filterBlankObjects(formData.education) : [],
    certifications: Array.isArray(formData.certifications) ? filterBlankObjects(formData.certifications) : [],
    years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience, 10) : null,

    // Patient-related flags
    accepts_new_patients: formData.accepts_new_patients === true,
    telehealth_available: formData.telehealth_available === true,

    // Hours and contact
    office_hours: officeHours,
    parking_info: formData.parking_info || '',
    website_url: formData.website_url || '',
    social_media: socialMedia,

    // Payment and accessibility
    payment_methods: Array.isArray(formData.payment_methods) ? filterBlankStrings(formData.payment_methods) : [],
    accessibility_options: Array.isArray(formData.accessibility_options) ? filterBlankStrings(formData.accessibility_options) : [],

    // Optional fields
    verification_info: formData.verification_info || null
  };
}

/**
 * Save a provider (create or update)
 * @param {string} token - JWT token
 * @param {Object} provider - Provider data
 * @returns {Promise<Object>} - Saved provider
 */
export async function saveProvider(token, providerData) {
  if (!token) {
    throw new Error('Authentication required');
  }

  console.log("form data to save is ", providerData)
    
  // Transform form data to API format
  const apiData = formatFormDataForApi(providerData);

  console.log("api data to send to server ", apiData)
    
  const method = providerData.id ? 'PUT' : 'POST';
  const url = providerData.id 
    ? `${API_BASE_URL}/providers/protected/${providerData.id}`
    : `${API_BASE_URL}/providers/protected`;
      
  const requestBody = JSON.stringify(apiData);
  
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'X-API-Key': API_KEY
    },
    body: requestBody
  });
    
  // Better error handling
  if (!response.ok) {
    const errorText = await response.text();
    console.error('=== SERVICE: Error response ===');
    console.error('Error text:', errorText);
    
    let errorMessage = `HTTP error! status: ${response.status}`;
    
    try {
      const errorData = JSON.parse(errorText);
      if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch (e) {
      // If can't parse as JSON, use the raw text
      if (errorText) {
        errorMessage = errorText;
      }
    }
    
    throw new Error(errorMessage);
  }
  
  const result = await response.json();
    
  return result;
}

/**
 * Claim a provider profile
 * @param {string} token - JWT authentication token
 * @param {Object|FormData} claimData - Provider claim data or FormData for file upload
 * @param {boolean} isFormData - Whether the data is FormData (for file uploads)
 * @returns {Promise<Object>} - Claim submission result
 */
export async function claimProviderProfile(token, claimData, isFormData = false) {
  const headers = {
    'Authorization': `Bearer ${token}`,
    'X-API-Key': API_KEY
  };
  
  // Don't set Content-Type for FormData (browser will set it with boundary)
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  
  const response = await fetch(`${API_BASE_URL}/providers/claim`, {
    method: 'POST',
    headers: headers,
    body: isFormData ? claimData : JSON.stringify(claimData)
  });
  
  if (!response.ok) {
    if (response.status === 400) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Invalid claim data');
    } else if (response.status === 409) {
      throw new Error('This provider profile has already been claimed');
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return await response.json();
}

/**
 * Upload a provider profile image
 * @param {string} token - JWT authentication token
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - Response with image URL info or error details
 */
export async function uploadProviderProfileImage(token, imageFile) {
  // Create a FormData object to send the file
  const formData = new FormData();
  formData.append('file', imageFile);
  
  try {
    // Use the correct API path for provider profile image upload
    const apiUrl = `${API_BASE_URL}/providers/protected/profile-image`;    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: formData
    });
        
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      // Return an object with error info instead of throwing
      return {
        success: false,
        error: errorMessage,
        serverIssue: true
      };
    }
    
    // Handle successful response
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        return {
          success: true,
          image_url: data.presigned_url || data.profile_image
        };
      } else {
        const textResponse = await response.text();
        return { 
          success: true,
          message: 'Image uploaded successfully', 
          text_response: textResponse 
        };
      }
    } catch (parseError) {
      console.error('Error parsing success response:', parseError);
      return {
        success: false,
        error: 'Could not parse server response',
        serverIssue: true
      };
    }
  } catch (error) {
    console.error('Error uploading provider profile image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get a provider's profile image URL
 * @param {string} providerId - The ID of the provider
 * @returns {Promise<Object>} - URL of the profile image or error details
 */
export async function getProviderProfileImage(providerId) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/${providerId}/profile-image`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
      // Note: No Authorization header since this is a public endpoint
    });
        
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    
    return {
      success: true,
      image_url: data.presigned_url || data.profile_image || null
    };
  } catch (error) {
    console.error('Error in getProviderProfileImage:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get the current provider's profile image URL
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @returns {Promise<Object>} - URL of the profile image or error details
 */
export async function getCurrentProviderProfileImage(token, providerId) {
  if (!providerId) {
    return {
      success: false,
      error: "Provider ID is required",
      serverIssue: false
    };
  }
  
  try {
    // We can use the public endpoint since we already know our provider ID
    return await getProviderProfileImage(providerId);
  } catch (error) {
    console.error('Error in getCurrentProviderProfileImage:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Upload a provider facility image
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @param {File} imageFile - The image file to upload
 * @param {Object} metadata - Image metadata (type, description, display order)
 * @returns {Promise<Object>} - Response with image details or error
 */
export async function uploadProviderImage(token, providerId, imageFile, metadata = {}) {
  if (!token || !providerId || !imageFile) {
    return {
      success: false,
      error: "Missing required parameters",
      serverIssue: false
    };
  }
  
  // Create a FormData object to send the file
  const formData = new FormData();
  formData.append('file', imageFile);
  
  // Build the query string for metadata
  const params = new URLSearchParams();
  if (metadata.image_type) params.append('image_type', metadata.image_type);
  if (metadata.description) params.append('description', metadata.description);
  if (metadata.display_order !== undefined) params.append('display_order', metadata.display_order);
  
  try {
    // ✅ FIXED: Added /protected to URL
    const apiUrl = `${API_BASE_URL}/providers/protected/${providerId}/images?${params.toString()}`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: formData
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      return {
        success: false,
        error: errorMessage,
        serverIssue: true
      };
    }
    
    const data = await response.json();
    return {
      success: true,
      image: data
    };
  } catch (error) {
    console.error('Error uploading provider image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get all images for a provider
 * @param {string} providerId - The provider's ID
 * @param {string} typeFilter - Optional filter by image type
 * @returns {Promise<Object>} - Array of provider images or error
 */
export async function getProviderImages(providerId, typeFilter = null) {
  try {
    // ✅ This one stays PUBLIC (no /protected)
    let apiUrl = `${API_BASE_URL}/providers/${providerId}/images`;
    
    if (typeFilter) {
      apiUrl += `?type_filter=${encodeURIComponent(typeFilter)}`;
    }
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      images: data
    };
  } catch (error) {
    console.error('Error fetching provider images:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Get a specific provider image by ID
 * @param {string} providerId - The provider's ID
 * @param {string} imageId - The image ID
 * @returns {Promise<Object>} - Image details or error
 */
export async function getProviderImage(providerId, imageId) {
  try {
    // ✅ This one stays PUBLIC (no /protected)
    const apiUrl = `${API_BASE_URL}/providers/${providerId}/images/${imageId}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    const data = await response.json();
    return {
      success: true,
      image: data
    };
  } catch (error) {
    console.error('Error fetching provider image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Update a provider image's metadata
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @param {string} imageId - The image ID
 * @param {Object} metadata - Updated metadata (type, description, display order)
 * @returns {Promise<Object>} - Updated image details or error
 */
export async function updateProviderImage(token, providerId, imageId, metadata) {
  try {
    // ✅ FIXED: Added /protected to URL
    const apiUrl = `${API_BASE_URL}/providers/protected/${providerId}/images/${imageId}`;
    
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(metadata)
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      const errorData = await response.json().catch(() => ({}));
      
      if (errorData.error) {
        errorMessage = errorData.error;
      }
      
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return {
      success: true,
      image: data
    };
  } catch (error) {
    console.error('Error updating provider image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Delete a provider image
 * @param {string} token - JWT authentication token
 * @param {string} providerId - The provider's ID
 * @param {string} imageId - The image ID to delete
 * @returns {Promise<Object>} - Success status or error
 */
export async function deleteProviderImage(token, providerId, imageId) {
  try {
    // ✅ FIXED: Added /protected to URL
    const apiUrl = `${API_BASE_URL}/providers/protected/${providerId}/images/${imageId}`;
    
    const response = await fetch(apiUrl, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      }
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        }
      } catch (e) {
        // Ignore parse errors for non-JSON responses
      }
      
      throw new Error(errorMessage);
    }
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error deleting provider image:', error);
    return {
      success: false,
      error: error.message,
      serverIssue: true
    };
  }
}

/**
 * Initiate provider verification process
 * @param {string} token - JWT authentication token
 * @param {string} providerId - Provider ID to initiate verification for
 * @param {Object} verificationData - Verification data (documents, etc.)
 * @returns {Promise<Object>} - Verification initiation result
 */
export async function initiateProviderVerification(token, providerId, verificationData = {}) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/protected/${providerId}/initiate-verification`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(verificationData)
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
    
    const data = await response.json();

    return {
      success: true,
      verification: data
    };
  } catch (error) {
    console.error('Error initiating provider verification:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Mark verification as submitted
 * @param {string} token - JWT authentication token
 * @param {Object} submissionData - Verification submission data
 * @returns {Promise<Object>} - Submission result
 */
export async function markVerificationSubmitted(token, submissionData = {}) {
  try {
    const apiUrl = `${API_BASE_URL}/providers/protected/verification/submitted`;
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(submissionData)
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      return {
        success: false,
        error: errorMessage
      };
    }

    // Backend returns just StatusCode::OK with no JSON body
    // So we don't try to parse JSON, just return success
    return {
      success: true
    };
  } catch (error) {
    console.error('Error marking verification as submitted:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Admin: Get all providers
 * @param {string} token - JWT authentication token
 * @param {Object} params - Query parameters (page, limit, status, etc.)
 * @returns {Promise<Object>} - List of providers or error
 */
export async function adminGetProviders(token, params = {}) {
  try {
    const queryParams = new URLSearchParams();
    
    // Add query parameters if provided
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    
    const queryString = queryParams.toString();
    const apiUrl = `${API_BASE_URL}/admin/providers${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      }
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
    
    const data = await response.json();
    return {
      success: true,
      providers: data.providers || data,
      total: data.total || 0,
      page: data.page || 1,
      totalPages: data.total_pages || 1
    };
  } catch (error) {
    console.error('Error fetching admin providers:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Admin: Update provider status
 * @param {string} token - JWT authentication token
 * @param {Object} statusData - Status update data (provider_id, status, notes, etc.)
 * @returns {Promise<Object>} - Update result
 */
export async function adminUpdateProviderStatus(token, statusData) {
  try {
    const apiUrl = `${API_BASE_URL}/admin/providers/status`;
    
    const response = await fetch(apiUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(statusData)
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } else {
          const textResponse = await response.text();
          if (textResponse) {
            errorMessage = `${errorMessage}: ${textResponse}`;
          }
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      
      return {
        success: false,
        error: errorMessage
      };
    }
    
    const data = await response.json();
    return {
      success: true,
      provider: data
    };
  } catch (error) {
    console.error('Error updating provider status:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Generate AI-powered "About Me" content for a provider
 * @param {string} token - JWT authentication token
 * @param {Object} formData - Provider form data
 * @param {string} formData.provider_name - Provider's name
 * @param {string} formData.provider_type - Type of provider (e.g., "Cardiologist")
 * @param {Array<string>} formData.specializations - Array of specializations
 * @param {Array<string>} formData.procedures - Array of procedures
 * @param {Array<Object>} formData.education - Array of education objects
 * @param {Array<Object>} formData.certifications - Array of certification objects
 * @param {number|null} formData.years_of_experience - Years of experience
 * @param {Array<string>} formData.spoken_languages - Array of languages spoken
 * @param {Array<string>} formData.tags - Array of tags
 * @param {string} tone - Tone of writing: "professional", "friendly", or "formal"
 * @param {string} length - Length: "short", "medium", or "detailed"
 * @param {string} language - Language: "english", "hindi", "telugu", etc.
 * @returns {Promise<Object>} - Generated about me text or error
 */
export async function generateAboutMe(token, formData, tone = 'professional', length = 'medium', language = 'english') {
  try {
    const payload = {
      // User preferences
      tone,
      length,
      language,
      
      // Provider data from form
      provider_name: formData.provider_name || '',
      provider_type: formData.provider_type || '',
      specializations: formData.specializations || [],
      procedures: formData.procedures || [],
      education: formData.education || [],
      certifications: formData.certifications || [],
      years_of_experience: formData.years_of_experience || null,
      spoken_languages: formData.spoken_languages || [],
      tags: formData.tags || []
    };

    const apiUrl = `${API_BASE_URL}/providers/protected/generate-about-me`;
    
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-API-Key': API_KEY
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! Status: ${response.status}`;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        }
      } catch (parseError) {
        console.error('Error parsing error response:', parseError);
      }
      return {
        success: false,
        error: errorMessage
      };
    }

    const data = await response.json();

    console.log("data is ", data);

    return {
      success: true,
      about_me: data.about_me
    };
  } catch (error) {
    console.error('Error generating about me:', error);
    return {
      success: false,
      error: error.message
    };
  }
}